import React, { useState, useEffect } from 'react';
import { GlobalButton } from './GlobalButton';
import { GlobalInput } from './GlobalInput';
import { Toast } from './Toast';
import { useLocale } from '../context/DynamicLocaleProvider';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import styles from './CompanyIntroductionModal.module.css';

interface CompanyIntroductionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCompanyAdded: () => void;
}

export const CompanyIntroductionModal: React.FC<CompanyIntroductionModalProps> = ({
  isOpen,
  onClose,
  onCompanyAdded
}) => {
  const { t } = useLocale();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    name: '',
    website: '',
    address_street: '',
    address_house_number: '',
    address_zip_code: '',
    address_city: '',
    address_country: ''
  });

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, onClose]);

  // Clear form when modal is closed
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        name: '',
        website: '',
        address_street: '',
        address_house_number: '',
        address_zip_code: '',
        address_city: '',
        address_country: ''
      });
      setError(null);
      setSuccess(null);
    }
  }, [isOpen]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation - all fields are required
    if (!formData.name.trim()) {
      setError(t('company_modal_error_name_required') || 'Company name is required');
      return;
    }
    if (!formData.website.trim()) {
      setError(t('company_modal_error_website_required') || 'Website is required');
      return;
    }
    // Address fields are optional - removed strict validation

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const companyData: any = {
        name: formData.name.trim()
      };

      // Only add optional fields if they have values
      if (formData.website.trim()) {
        companyData.website = formData.website.trim();
      }
      if (formData.address_street.trim()) {
        companyData.address_street = formData.address_street.trim();
      }
      if (formData.address_house_number.trim()) {
        companyData.address_house_number = formData.address_house_number.trim();
      }
      if (formData.address_zip_code.trim()) {
        companyData.address_zip_code = formData.address_zip_code.trim();
      }
      if (formData.address_city.trim()) {
        companyData.address_city = formData.address_city.trim();
      }
      if (formData.address_country.trim()) {
        companyData.address_country = formData.address_country.trim();
      }

      await addDoc(collection(db, 'companies'), companyData);

      setSuccess(t('company_modal_success') || 'Company created successfully');
      
      // Wait a moment for the toast to be visible
      setTimeout(() => {
        onClose();
        setFormData({
          name: '',
          website: '',
          address_street: '',
          address_house_number: '',
          address_zip_code: '',
          address_city: '',
          address_country: ''
        });
        setLoading(false);
        setSuccess(null);
        onCompanyAdded();
      }, 1500);

    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Failed to create company:', err);
      }
      setError(err instanceof Error ? err.message : t('company_modal_error_create_failed') || 'Failed to create company');
      setLoading(false);
    }
  };

  const clearError = () => setError(null);
  const clearSuccess = () => setSuccess(null);

  if (!isOpen) return null;

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2>{t('company_modal_title')}</h2>
          <button onClick={onClose} className={styles.closeButton}>
            ×
          </button>
        </div>

        <div className={styles.content}>

          
          <form onSubmit={handleSubmit} className={styles.formFields}>
            {/* Company Name - Required */}
            <GlobalInput
              label={t('company_modal_name_label') || 'Company Name'}
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder={t('company_modal_name_placeholder') || 'Enter company name'}
              required
              disabled={loading}
            />

            {/* Website - Required */}
            <GlobalInput
              label={t('company_modal_website_label') || 'Website'}
              value={formData.website}
              onChange={(e) => handleInputChange('website', e.target.value)}
              placeholder={t('company_modal_website_placeholder') || 'https://example.com'}
              type="url"
              required
              disabled={loading}
            />

            {/* Manual Address Fields */}
            <div className={styles.addressRow}>
              <GlobalInput
                label={t('company_modal_street_label') || 'Street'}
                value={formData.address_street}
                onChange={(e) => handleInputChange('address_street', e.target.value)}
                placeholder={t('company_modal_street_placeholder') || 'Street name'}
                disabled={loading}
              />
              <GlobalInput
                label={t('company_modal_house_number_label') || 'House Number'}
                value={formData.address_house_number}
                onChange={(e) => handleInputChange('address_house_number', e.target.value)}
                placeholder={t('company_modal_house_number_placeholder') || '123'}
                disabled={loading}
              />
            </div>
            <div className={styles.addressRow}>
              <GlobalInput
                label={t('company_modal_zip_code_label') || 'ZIP Code'}
                value={formData.address_zip_code}
                onChange={(e) => handleInputChange('address_zip_code', e.target.value)}
                placeholder={t('company_modal_zip_code_placeholder') || '12345'}
                disabled={loading}
              />
              <GlobalInput
                label={t('company_modal_city_label') || 'City'}
                value={formData.address_city}
                onChange={(e) => handleInputChange('address_city', e.target.value)}
                placeholder={t('company_modal_city_placeholder') || 'City name'}
                disabled={loading}
              />
            </div>
            <GlobalInput
              label={t('company_modal_country_label') || 'Country'}
              value={formData.address_country}
              onChange={(e) => handleInputChange('address_country', e.target.value)}
              placeholder={t('company_modal_country_placeholder') || 'Country name'}
              disabled={loading}
            />

            {/* Actions */}
            <div className={styles.actions}>
              <GlobalButton
                type="button"
                onClick={onClose}
                disabled={loading}
                variant="secondary"
              >
                {t('company_modal_cancel_button') || 'Cancel'}
              </GlobalButton>
              <GlobalButton
                type="submit"
                disabled={loading || !formData.name.trim() || !formData.website.trim()}
                isLoading={loading}
                variant="primary"
              >
                {t('company_modal_create_button') || 'Create Company'}
              </GlobalButton>
            </div>
          </form>
        </div>

        {error && (
          <Toast
            message={error}
            type="error"
            isVisible={true}
            onClose={clearError}
          />
        )}

        {success && (
          <Toast
            message={success}
            type="success"
            isVisible={true}
            onClose={clearSuccess}
          />
        )}
      </div>
    </div>
  );
};